if (!customElements.get('mission-timeline')) {
  class MissionTimeline extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.scrollRaf = null;
      this.scrollbarRaf = null;
      this.scrollbarResizeObserver = null;

      this.onClick = this.onClick.bind(this);
      this.onListScroll = this.onListScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.updateScrollbar = this.updateScrollbar.bind(this);
    }

    connectedCallback() {
      this.mediaItems = Array.from(this.querySelectorAll('.mission-timeline__media-item'));
      this.listItems = Array.from(this.querySelectorAll('.mission-timeline__item'));
      this.accordionItems = Array.from(this.querySelectorAll('.mission-timeline__accordion-item'));
      this.listScroll = this.querySelector('.mission-timeline__list-scroll');
      this.list = this.querySelector('.mission-timeline__list');
      this.scrollbar = this.querySelector('.mission-timeline__scrollbar');
      this.scrollbarThumb = this.querySelector('.mission-timeline__scrollbar-thumb');
      this.media = this.querySelector('.mission-timeline__media');
      this.desktopQuery = window.matchMedia('(min-width: 768px)');

      if (!this.mediaItems.length) return;

      this.setActiveIndex(this.getInitialIndex(), {
        force: true,
        updateAccordion: this.hasActiveAccordionItem(),
      });
      this.addEventListener('click', this.onClick);

      if (this.listScroll) {
        this.listScroll.addEventListener('scroll', this.onListScroll, { passive: true });

        this.initScrollbar();
      }

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);

      if (this.listScroll) {
        this.listScroll.removeEventListener('scroll', this.onListScroll);
      }

      this.scrollbarResizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollRaf);
      cancelAnimationFrame(this.scrollbarRaf);

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    getInitialIndex() {
      const activeItem = this.querySelector(
        '.mission-timeline__accordion-item.is-active, .mission-timeline__item.is-active'
      );
      const index = Number(activeItem?.dataset.index);

      return Number.isNaN(index) ? 0 : index;
    }

    hasActiveAccordionItem() {
      return this.accordionItems.some((item) => item.classList.contains('is-active'));
    }

    onClick(event) {
      const accordionToggle = event.target.closest('.mission-timeline__accordion-toggle');
      const accordionSubheading = event.target.closest('.mission-timeline__accordion-subheading');
      const listItem = event.target.closest('.mission-timeline__item');

      if (accordionToggle && this.contains(accordionToggle)) {
        this.toggleAccordionItem(accordionToggle);
        return;
      }

      if (accordionSubheading && this.contains(accordionSubheading)) {
        this.toggleAccordionItem(accordionSubheading);
        return;
      }

      if (listItem && this.contains(listItem) && this.desktopQuery.matches) {
        this.activateListItem(listItem);
      }
    }

    toggleAccordionItem(trigger) {
      const item = trigger.closest('.mission-timeline__accordion-item');
      const toggle = item?.querySelector('.mission-timeline__accordion-toggle');
      const index = Number(item?.dataset.index);

      if (!toggle || Number.isNaN(index)) return;

      if (item.classList.contains('is-active')) {
        item.classList.remove('is-active');
        toggle.setAttribute('aria-expanded', 'false');
        return;
      }

      this.setActiveIndex(index, { force: true });
    }

    activateListItem(item) {
      const index = Number(item.dataset.index);

      if (Number.isNaN(index)) return;

      this.setActiveIndex(index, { force: true });
      this.scrollListItemIntoView(item);
    }

    onListScroll() {
      if (!this.desktopQuery.matches || !this.listScroll) return;

      this.updateScrollbar();

      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        const index = this.getActiveIndexFromScroll();

        if (index !== null) {
          this.setActiveIndex(index);
        }
      });
    }

    initScrollbar() {
      if (!this.scrollbar || !this.scrollbarThumb) return;

      if (window.ResizeObserver) {
        this.scrollbarResizeObserver = new ResizeObserver(this.updateScrollbar);
        this.scrollbarResizeObserver.observe(this.listScroll);

        if (this.list) {
          this.scrollbarResizeObserver.observe(this.list);
        }
      }

      this.updateScrollbar();
    }

    updateScrollbar() {
      if (!this.listScroll || !this.scrollbar || !this.scrollbarThumb) return;

      cancelAnimationFrame(this.scrollbarRaf);
      this.scrollbarRaf = requestAnimationFrame(() => {
        const { scrollHeight, clientHeight, scrollTop } = this.listScroll;
        const trackHeight = clientHeight;

        if (scrollHeight <= clientHeight + 1) {
          this.scrollbar.classList.add('is-hidden');
          return;
        }

        this.scrollbar.classList.remove('is-hidden');
        this.scrollbar.style.height = `${trackHeight}px`;

        const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 40);
        const maxThumbTop = trackHeight - thumbHeight;
        const scrollRatio = scrollTop / (scrollHeight - clientHeight);

        this.scrollbarThumb.style.height = `${thumbHeight}px`;
        this.scrollbarThumb.style.top = `${scrollRatio * maxThumbTop}px`;
      });
    }

    getActiveIndexFromScroll() {
      const maxScrollTop = this.listScroll.scrollHeight - this.listScroll.clientHeight;
      const scrollTop = this.listScroll.scrollTop;
      const edgeThreshold = 32;

      if (scrollTop <= edgeThreshold) return 0;
      if (maxScrollTop > 0 && maxScrollTop - scrollTop <= edgeThreshold) {
        return this.listItems.length - 1;
      }

      const rootRect = this.listScroll.getBoundingClientRect();
      const rootCenter = rootRect.top + rootRect.height / 2;
      let closestIndex = null;
      let closestDistance = Infinity;

      this.listItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const index = Number(item.dataset.index);

        if (Number.isNaN(index) || rect.bottom < rootRect.top || rect.top > rootRect.bottom) {
          return;
        }

        const distance = Math.abs(rect.top + rect.height / 2 - rootCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    scrollListItemIntoView(item) {
      if (!this.listScroll || !item) return;

      const index = Number(item.dataset.index);
      const scrollRect = this.listScroll.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemTop = this.listScroll.scrollTop + itemRect.top - scrollRect.top;
      const maxScrollTop = Math.max(0, this.listScroll.scrollHeight - this.listScroll.clientHeight);
      const centeredTop = itemTop - (this.listScroll.clientHeight - item.offsetHeight) / 2;
      const top = index === 0 ? 0 : Math.min(Math.max(0, centeredTop), maxScrollTop);

      this.listScroll.scrollTo({ top, behavior: 'auto' });
      this.updateScrollbar();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const blockId = event.detail.blockId;
      const listItem = this.querySelector(`.mission-timeline__item[data-tab="tab-${blockId}"]`);
      const accordionItem = this.querySelector(`.mission-timeline__accordion-item[data-tab="tab-${blockId}"]`);
      const activeItem = this.desktopQuery.matches ? listItem : accordionItem;
      const index = Number(activeItem?.dataset.index);

      if (Number.isNaN(index)) return;

      this.setActiveIndex(index, { force: true });

      if (listItem && this.desktopQuery.matches) {
        this.scrollListItemIntoView(listItem);
      }
    }

    setActiveIndex(index, options = {}) {
      if (index < 0 || index >= this.mediaItems.length) return;
      if (!options.force && index === this.activeIndex) return;

      this.activeIndex = index;

      this.mediaItems.forEach((item, itemIndex) => {
        item.classList.toggle('is-visible', itemIndex === index);
      });

      this.listItems.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });

      if (options.updateAccordion !== false) {
        this.accordionItems.forEach((item, itemIndex) => {
          const isActive = itemIndex === index;
          const toggle = item.querySelector('.mission-timeline__accordion-toggle');

          item.classList.toggle('is-active', isActive);
          toggle?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });
      }

      this.updateMediaRatio(index);
      this.updateScrollbar();
    }

    updateMediaRatio(index) {
      const mediaRatio = this.mediaItems[index]?.dataset.mediaRatio;

      if (this.media && this.dataset.mediaSize === 'adapt' && mediaRatio) {
        this.media.style.setProperty('--mission-timeline-media-ratio', mediaRatio);
      }
    }
  }

  customElements.define('mission-timeline', MissionTimeline);
}
